import AddIcon from "@mui/icons-material/Add";
import ChatIcon from "@mui/icons-material/Chat";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PersonIcon from "@mui/icons-material/Person";
import PsychologyIcon from "@mui/icons-material/Psychology";
import {
  Box,
  Button,
  Dropdown,
  Menu,
  MenuButton,
  MenuItem,
  Tooltip,
} from "@mui/joy";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { IoFlagOutline } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import { useModelStore } from "../stores/modelStore";
import { ModelSettingsModal } from "./ModelSettingsModal";

interface ConversationControlsProps {
  characterId: string;
  onOpenConversationsDrawer: () => void;
  onOpenPersonaModal: () => void;
}

export function ConversationControls({
  characterId,
  onOpenConversationsDrawer,
  onOpenPersonaModal,
}: ConversationControlsProps): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isButtonCooling, setIsButtonCooling] = useState(false);
  const [modelSettingsOpen, setModelSettingsOpen] = useState(false);
  const selectedModel = useModelStore((state) => state.selectedModel);

  const handleOpenDrawer = () => {
    if (isButtonCooling) return;

    setIsButtonCooling(true);
    onOpenConversationsDrawer();

    // Reset the button cooldown
    setTimeout(() => {
      setIsButtonCooling(false);
    }, 1000);
  };

  return (
    <Box
      sx={{
        display: "flex",
        gap: 1,
        alignItems: "center",
        fontSize: "1.3rem",
        fontWeight: 500,
      }}
    >
      <Tooltip title={t("modelSelector.model")} placement="bottom" size="sm">
        <Button
          variant="soft"
          size="sm"
          onClick={() => setModelSettingsOpen(true)}
          startDecorator={<PsychologyIcon sx={{ fontSize: "1rem" }} />}
          sx={{
            borderRadius: "8px",
            backgroundColor:
              selectedModel === "chatgpt"
                ? "#e8f5e8" // Light green background
                : selectedModel === "recommended"
                  ? "#f5f5f5" // Light grey background
                  : selectedModel === "povchat"
                    ? "#f3e8ff" // Light purple background (for povchat)
                    : selectedModel === "deepseek"
                      ? "#fff3e0" // Light orange background (deepseek)
                      : selectedModel === "mistral"
                        ? "#fff3e0" // Light orange background (mistral)
                        : "#e3f2fd", // Light blue background (wizardlm, hermes, claude, llama models)
            color:
              selectedModel === "chatgpt"
                ? "#2e7d32" // Dark green text
                : selectedModel === "recommended"
                  ? "#666666" // Dark grey text
                  : selectedModel === "povchat"
                    ? "#7b1fa2" // Dark purple text (for povchat)
                    : selectedModel === "deepseek"
                      ? "#ef6c00" // Orange text (deepseek)
                      : selectedModel === "mistral"
                        ? "#ef6c00" // Orange text (mistral)
                        : "#1565c0", // Dark blue text (wizardlm, hermes, claude, llama models)
            "&:hover": {
              backgroundColor:
                selectedModel === "chatgpt"
                  ? "#c8e6c9" // Medium green hover
                  : selectedModel === "recommended"
                    ? "#e0e0e0" // Medium grey hover
                    : selectedModel === "povchat"
                      ? "#e1bee7" // Medium purple hover (for povchat)
                      : selectedModel === "deepseek"
                        ? "#ffe0b2" // Medium orange hover (deepseek)
                        : selectedModel === "mistral"
                          ? "#ffe0b2" // Medium orange hover (mistral)
                          : "#bbdefb", // Medium blue hover (wizardlm, hermes, claude)
            },
            "&:active": {
              backgroundColor:
                selectedModel === "chatgpt"
                  ? "#a5d6a7" // Darker green active
                  : selectedModel === "recommended"
                    ? "#d0d0d0" // Darker grey active
                    : selectedModel === "povchat"
                      ? "#ce93d8" // Darker purple active (for povchat)
                      : selectedModel === "deepseek"
                        ? "#ffcc80" // Darker orange active (deepseek)
                        : selectedModel === "mistral"
                          ? "#ffcc80" // Darker orange active (mistral)
                          : "#90caf9", // Darker blue active (wizardlm, hermes, claude)
            },
            // Show only icon on small screens, text on larger screens
            minWidth: { xs: 32, sm: "auto" },
            px: { xs: 0.5, sm: 1.5 },
            "& .MuiButton-startDecorator": {
              margin: { xs: 0, sm: "0 0.5rem 0 0" },
            },
          }}
        >
          <Box
            sx={{
              fontSize: "0.75rem",
              fontWeight: 600,
            }}
          >
            {selectedModel === "chatgpt"
              ? t("modelSelector.chatgpt")
              : selectedModel === "povchat"
                ? t("modelSelector.roleplay")
                : selectedModel === "deepseek"
                  ? t("modelSelector.deepseek")
                  : selectedModel === "mistral"
                    ? t("modelSelector.mistral")
                    : selectedModel === "wizardlm"
                      ? t("modelSelector.wizardlm")
                      : selectedModel === "hermes"
                        ? t("modelSelector.hermes")
                        : selectedModel === "claude"
                          ? t("modelSelector.claude")
                          : selectedModel === "llama8b"
                            ? t("modelSelector.llama8b")
                            : selectedModel === "lunaris"
                              ? t("modelSelector.lunaris")
                              : selectedModel === "llama70b"
                                ? t("modelSelector.llama70b")
                                : selectedModel === "gemini"
                                  ? t("modelSelector.gemini")
                                  : selectedModel === "glm"
                                    ? t("modelSelector.glm")
                                    : t("modelSelector.recommended")}
          </Box>
        </Button>
      </Tooltip>
      <Dropdown>
        <Tooltip
          title={t("conversationControls.moreOptions")}
          placement="bottom"
          size="sm"
        >
          <MenuButton
            variant="plain"
            size="sm"
            aria-label={t("conversationControls.moreOptions")}
            sx={{
              minWidth: 32,
              p: 0.5,
              border: "1px solid transparent",
              borderRadius: "8px",
              backgroundColor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.05)"
                  : "transparent",
              "&:hover": {
                borderColor: "divider",
                backgroundColor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.1)"
                    : "rgba(0, 0, 0, 0.04)",
              },
            }}
          >
            <MoreVertIcon sx={{ fontSize: "1.3rem" }} />
          </MenuButton>
        </Tooltip>
        <Menu
          sx={{
            boxShadow: "0 2px 12px 0 rgba(0,0,0,0.2)",
            borderRadius: "md",
            border: "none",
            "--List-decorator-size": "0px",
            right: 0,
            transformOrigin: "top right",
            "& .MuiMenuItem-root": {
              fontSize: "1rem",
              border: "none",
              borderRadius: "8px",
              my: 0.5,
              mx: 0.5,
              backgroundColor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.04)"
                  : "transparent",
              "&:hover": {
                backgroundColor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.08)"
                    : "rgba(0, 0, 0, 0.04)",
              },
            },
          }}
          placement="bottom-end"
        >
          <MenuItem onClick={handleOpenDrawer} disabled={isButtonCooling}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <AddIcon sx={{ fontSize: "1.2rem" }} />
              {t("conversationControls.newChat")}
            </Box>
          </MenuItem>
          <MenuItem onClick={handleOpenDrawer} disabled={isButtonCooling}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <ChatIcon sx={{ fontSize: "1.2rem" }} />
              {t("conversationControls.allChats")}
            </Box>
          </MenuItem>
          <MenuItem
            onClick={() => {
              onOpenPersonaModal();
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <PersonIcon sx={{ fontSize: "1.2rem" }} />
              {t("conversationControls.persona")}
            </Box>
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (!characterId) return;
              navigate(`/character/${characterId}/report`);
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IoFlagOutline size="1.2rem" />
              {t("conversationControls.report")}
            </Box>
          </MenuItem>
        </Menu>
      </Dropdown>

      <ModelSettingsModal
        open={modelSettingsOpen}
        onClose={() => setModelSettingsOpen(false)}
      />
    </Box>
  );
}
